import React, { useState, useContext, useEffect, useRef, useCallback } from 'react'
import { Form } from 'semantic-ui-react'
import * as R from 'ramda'
import dayjs from 'dayjs'

import { toast } from 'libs/toast'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { AudienceRuleForm } from 'organisms/AudienceRuleForm'
import { transformAudienceRuleToUiState } from 'domains/email'
import useAxios, { mutate } from 'hooks/useAxios'
import { getSurveyTokenFromId } from 'reducers'
import { Loader } from 'shared/components/Loader'
import { surveySettingContext } from 'pages/SurveySettings/context'
import { CustomerListWidget } from 'organisms/CustomerListWidget/container'
import { FrequencySetting } from 'components/FrequencySetting'
import { DeliveryWindowSetting } from 'organisms/DeliveryWindowSetting'
import { DistributionSettingContainer } from 'organisms/DistributionSetting/container'
import { csvDownload } from 'libs/csv-download'
import { ImportCustomerContainer } from 'organisms/ImportCustomerButton/container'
import useSaveUserActivityLog, { ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import { TrialWarningMessage } from 'components/TrialWarningMessage'
import { getWorkspaceTimezone } from 'libs/date'

const BASE_FIELDS = [
  {
    key: 'customer',
    label: 'Customer',
    field: 'email',
    isCoreField: true,
  },
  {
    key: 'company',
    label: 'Company',
    field: 'company',
    isCoreField: true,
  },
  {
    key: 'customer.tag',
    label: 'Tags',
    field: 'customer.tag',
    isCoreField: true,
  },
]
const FREQUENCY_OPTIONS = [
  {
    text: '1 week',
    value: 7,
  },
  {
    text: '2 weeks',
    value: 14,
  },
  {
    text: '1 month',
    value: 30,
  },
  {
    text: '2 months',
    value: 60,
  },
  {
    text: '3 months',
    value: 91,
  },
  {
    text: '6 months',
    value: 183,
  },
  {
    text: '12 months',
    value: 365,
  },
]
export default function OngoingEmailDeliverySettingContainer() {
  const surveyToken = useSurveyToken()
  const [audienceRulesState, setAudienceRulesState] = useState()
  const [frequencyDay, setFrequencyDay] = useState(0)
  const [isCSVDownloading, setIsCSVDownloading] = useState(false)
  const [window, setWindow] = useState({
    send_window_days: [],
    send_window_start: null,
  })
  const [distribution, setDistribution] = useState()
  const { setContext } = useContext(surveySettingContext)
  // PET-2084: track dirty in a LOCAL flag and sync to the shared context in an
  // effect (not synchronously inside change handlers — see generic-embed).
  const [isFormDirty, setIsFormDirty] = useState(false)
  useEffect(() => {
    setContext({ isSurveyBuilderDirty: isFormDirty })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormDirty])
  const workspaceTimezone = getWorkspaceTimezone()

  const advancedFilterFields = useAxios({
    url: 'reports/preload-advanced-filters/',
  })

  const getAudienceRulesApi = useAxios({
    url: `survey/survey/${surveyToken}/survey-sending-rules/`,
  })

  const emailDeliverySetting = useAxios({
    url: `survey/email-delivery-setting/${surveyToken}/`,
  })

  const saveAudienceRulesApi = useAxios({
    method: 'post',
    url: `survey/survey/${surveyToken}/survey-sending-rules/`,
    skip: true,
  })

  const customersMatchingAudienceRulesApi = useAxios({
    method: 'post',
    url: `survey/survey/${surveyToken}/audience/`,
    skip: true,
  })

  const saveEmailDeliverySettingApi = useAxios({
    method: 'patch',
    url: `survey/email-delivery-setting/${surveyToken}/`,
    skip: true,
  })

  const getAudienceRulesState = useCallback(() => {
    if (R.prop('audience_rules', getAudienceRulesApi.data) && advancedFilterFields.data) {
      const audienceRulesFields = getAudienceRulesFields(advancedFilterFields.data)
      const audienceRulesState = transformAudienceRuleToUiState(
        getAudienceRulesApi.data,
        audienceRulesFields
      )

      return audienceRulesState
    }
  }, [advancedFilterFields.data, getAudienceRulesApi.data])

  const _getLocalTime = useCallback(
    (time) => {
      const hour = parseInt(time.substring(0, 2))
      const day = dayjs().utc().set('hour', hour).set('minute', 0).set('second', 0)

      return day.tz(workspaceTimezone).format('HH:mm:ss')
    },
    [workspaceTimezone]
  )

  useEffect(() => {
    const audienceRulesState = getAudienceRulesState()
    if (audienceRulesState) {
      setAudienceRulesState(R.map(R.over(R.lensProp('field'), R.prop('value')), audienceRulesState))
    }
  }, [getAudienceRulesState])

  useEffect(() => {
    if (R.path(['send_window_start'], emailDeliverySetting.data)) {
      setFrequencyDay(emailDeliverySetting.data.frequency_day)
      setDistribution(emailDeliverySetting.data.distribution)
      setWindow({
        send_window_days: emailDeliverySetting.data.send_window_days,
        send_window_start: _getLocalTime(emailDeliverySetting.data.send_window_start),
      })
    }
  }, [emailDeliverySetting.data, _getLocalTime])

  function _getUtcTime(time) {
    const hour = parseInt(time.substring(0, 2))
    const day = dayjs().local().set('hour', hour).set('minute', 0).set('second', 0)

    return day.utc().format('HH:mm:ss')
  }

  function useSetHeaderSaveButtonLoading(audienceRulesState, loading) {
    const { setContext } = useContext(surveySettingContext)
    useEffect(() => {
      setContext({
        isSubmitting: loading,
      })

      return () => {
        setContext({
          isSubmitting: false,
        })
      }
    }, [audienceRulesState, loading, setContext])
  }

  function useSetCurrentSaveFunction(buttonRef) {
    const { setContext } = useContext(surveySettingContext)

    useEffect(() => {
      function saveAudienceRules() {
        if (buttonRef.current) {
          buttonRef.current.click()
        }
      }

      setContext({
        currentSaveFunction: saveAudienceRules,
      })
    }, [buttonRef, setContext])
  }

  const buttonRef = useRef()
  useSetCurrentSaveFunction(buttonRef)
  const isSaveButtonLoading = Boolean(saveAudienceRulesApi.loading || !getAudienceRulesApi.data)
  useSetHeaderSaveButtonLoading(audienceRulesState, isSaveButtonLoading)

  const setIsAudienceRulesChanged = useDisableSendButton(isSaveButtonLoading)

  const { surveyId } = useParams()
  const saveUserActivityLog = useSaveUserActivityLog()
  function handleSubmit() {
    const data = {
      audience_rules: audienceRulesState,
      survey_id: surveyId,
    }

    const emailDeliverySettingRequest = {
      distribution,
      frequency_day: frequencyDay,
      send_window_days: window.send_window_days,
      send_window_start: _getUtcTime(window.send_window_start),
    }

    Promise.all([
      saveAudienceRulesApi.fetch({ data }),
      saveEmailDeliverySettingApi.fetch({
        data: emailDeliverySettingRequest,
      }),
    ])
      .then((response) => {
        const [audienceRuleResponse, deliverySettingResponse] = response
        setIsAudienceRulesChanged(false)
        setIsFormDirty(false)
        mutate(
          `survey/survey/${surveyToken}/survey-sending-rules/`,
          audienceRuleResponse.data,
          false
        )
        mutate(`survey/email-delivery-setting/${surveyToken}/`, deliverySettingResponse.data, false)

        toast.success('Delivery settings were successfully updated.')

        saveUserActivityLog({
          action: ACTION_NAME.edit_survey_publish_delivery_page,
          target: `sur_${surveyId}`,
          requestBody: { ...data, ...emailDeliverySettingRequest },
          statusCode: 200,
        })
      })
      .catch((error) => {
        if (audienceRulesState === undefined) {
          toast.error('Please create an audience rule before saving the survey.')
        } else {
          toast.error(
            'Failed to save delivery settings. Please try again or contact Simplesat support.'
          )
        }

        saveUserActivityLog({
          action: ACTION_NAME.edit_survey_publish_delivery_page,
          target: `sur_${surveyId}`,
          requestBody: { ...data, ...emailDeliverySettingRequest },
          statusCode: error.response.status,
        })
      })
  }

  useEffect(() => {
    if (audienceRulesState) {
      const getCustomersMatchingAudienceRules = customersMatchingAudienceRulesApi.fetch
      getCustomersMatchingAudienceRules({
        data: {
          audience_rules: audienceRulesState,
          show_list: true,
        },
      })
    }
  }, [audienceRulesState, customersMatchingAudienceRulesApi.fetch])

  useEffect(() => {
    const customers = customersMatchingAudienceRulesApi.data
    if (customers) {
      setContext({
        numberOfRecipients: customers.number_of_customer,
      })
    }
  }, [customersMatchingAudienceRulesApi.data, setContext])

  if (
    !(
      advancedFilterFields.data &&
      getAudienceRulesApi.data &&
      R.path(['data', 'send_window_start'], emailDeliverySetting)
    )
  ) {
    return <Loader withContainer />
  }

  const allFields = R.map(
    (field) => ({
      label: field.label,
      value: field.field,
      key: field.key,
      isCoreField: field.isCoreField,
      field_type: field.field_type,
    }),
    getAudienceRulesFields(advancedFilterFields.data)
  )

  function handleAudienceRulesChange(audienceRules) {
    setIsAudienceRulesChanged(true)
    setIsFormDirty(true)
    setAudienceRulesState(audienceRules)
  }

  function getNumbersOfSpreadEmails(numbersOfCustomers) {
    if (R.isNil(numbersOfCustomers)) {
      return ''
    }
    if (window.send_window_days.length === 0) {
      return 0
    }

    return Math.ceil(((numbersOfCustomers / frequencyDay) * 7) / window.send_window_days.length)
  }

  function downloadAudienceRulesCSV(event) {
    event.preventDefault()
    setIsCSVDownloading(true)
    customersMatchingAudienceRulesApi
      .fetch({
        data: {
          audience_rules: audienceRulesState,
          show_list: true,
          all: true,
        },
      })
      .then((response) => {
        const customers = response.data.customers.map((row) => ({
          Name: row.name,
          Email: row.email,
          Company: row.company || '',
        }))

        const fileName = 'matching_audience_rules.csv'
        const header = ['Name', 'Email', 'Company']
        csvDownload(customers, fileName, header, { userInputFields: header })
        setIsCSVDownloading(false)
      })
  }

  return (
    <div className="grid ui stackable">
      <div className="twelve wide column">
        <h3>Sending audience</h3>
        <p>
          Create an audience based on the customers in your Simplesat account.{' '}
          <a href="https://help.simplesat.io/en/articles/2220503-direct-email-surveys#sending-audience">
            Learn more about audience rules.
          </a>
        </p>
        <TrialWarningMessage />
        <div className="mb4">
          <ImportCustomerContainer />
        </div>
        <Form onSubmit={handleSubmit} className="mr3" style={{ minWidth: 400 }}>
          <div className="grid ui stackable two column">
            <div className="column">
              <AudienceRuleForm
                rules={getAudienceRulesState()}
                fields={allFields}
                onChange={handleAudienceRulesChange}
                shouldShowAutocomplete
              />
            </div>
            <div className="column">
              <CustomerListWidget
                loading={customersMatchingAudienceRulesApi.loading && !isCSVDownloading}
                customers={customersMatchingAudienceRulesApi.data}
                audienceRules={audienceRulesState}
                downloadAudienceRulesCSV={downloadAudienceRulesCSV}
                isDownloading={isCSVDownloading}
              />
            </div>
          </div>
          <FrequencySetting
            defaultValue={emailDeliverySetting.data.frequency_day}
            onSelect={(value) => {
              setFrequencyDay(value)
              setIsFormDirty(true)
            }}
            options={FREQUENCY_OPTIONS}
          />
          <DeliveryWindowSetting
            defaultValue={{
              send_window_days: emailDeliverySetting.data.send_window_days,
              send_window_start: _getLocalTime(emailDeliverySetting.data.send_window_start),
            }}
            onChange={(value) => {
              setWindow(value)
              setIsFormDirty(true)
            }}
          />
          <DistributionSettingContainer
            defaultValue={emailDeliverySetting.data.distribution}
            onChange={(value) => {
              setDistribution(value)
              setIsFormDirty(true)
            }}
            audienceRules={audienceRulesState}
            getNumbersOfSpreadEmails={getNumbersOfSpreadEmails}
          />
          <button type="submit" hidden ref={buttonRef} />
        </Form>
      </div>
    </div>
  )
}

function useSurveyToken() {
  const { surveyId } = useParams()
  return useSelector((state) => getSurveyTokenFromId(state, surveyId))
}

function useDisableSendButton(loading) {
  const [isAudienceRulesChange, setIsAudienceRulesChanged] = useState(false)
  const { setContext } = useContext(surveySettingContext)

  useEffect(() => {
    if (isAudienceRulesChange || loading) {
      setContext({
        isEmailActionButtonDisable: true,
      })
    } else {
      setContext({
        isEmailActionButtonDisable: false,
      })
    }
  }, [isAudienceRulesChange, loading, setContext])

  return setIsAudienceRulesChanged
}

function getAudienceRulesFields(advancedFilterFields) {
  return BASE_FIELDS.concat(
    advancedFilterFields.filter(R.propEq('key', 'customer.attribute')).map((record) => {
      return {
        field: record.field,
        label: record.label,
        key: record.key,
        field_type: record.field_type,
      }
    })
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/OngoingEmailDeliverySetting/index.js